import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { RuntimeConfigService } from './service/config/runtime-config.service';
import { LoggerService } from './service/utils/logger.service';
import { isCapacitorNative } from './service/platform/capacitor-platform';
import { oidcDiscoveryState } from './oidc-discovery.state';
import {
  applyOpenIdDiscoveryDocument,
  fetchOpenIdDiscoveryDocument,
  needsNativeDiscoveryFetch,
} from './oidc-discovery.loader';

const DISCOVERY_RETRY_ATTEMPTS = 3;
const DISCOVERY_RETRY_DELAY_MS = 900;

/** Load OIDC discovery via Spring ({@code GET /api/auth/openid-configuration}) so JSON comes from the API proxy, not the SPA dev server (which may otherwise return HTML as 200). */
function backendOpenIdDiscoveryUrl(runtime: RuntimeConfigService): string {
  const api = runtime.apiBaseUrl;
  if (api.startsWith('http://') || api.startsWith('https://')) {
    return `${api.replace(/\/+$/, '')}/auth/openid-configuration`;
  }
  if (typeof window === 'undefined') {
    return '/api/auth/openid-configuration';
  }
  const path = api.startsWith('/') ? api : `/${api}`;
  return `${window.location.origin}${path.replace(/\/+$/, '')}/auth/openid-configuration`;
}

/** Standard OIDC discovery on the configured issuer (Supabase on this project). */
function issuerOpenIdDiscoveryUrl(issuer: string): string {
  return `${issuer.replace(/\/+$/, '')}/.well-known/openid-configuration`;
}

/**
 * Web loads discovery via the Spring proxy (dev-server CORS). Native uses the issuer
 * directly so Google/Apple sign-in does not depend on reaching the Atplane API first.
 */
function openIdDiscoveryUrls(runtime: RuntimeConfigService): string[] {
  const issuer = runtime.oidcIssuer;
  const backend = backendOpenIdDiscoveryUrl(runtime);
  if (isCapacitorNative() && issuer) {
    const direct = issuerOpenIdDiscoveryUrl(issuer);
    return direct === backend ? [direct] : [direct, backend];
  }
  if (issuer) {
    const direct = issuerOpenIdDiscoveryUrl(issuer);
    return direct === backend ? [backend] : [backend, direct];
  }
  return [backend];
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function loadDiscoveryDocument(oauth: OAuthService, discoveryUrl: string): Promise<void> {
  if (needsNativeDiscoveryFetch(discoveryUrl)) {
    const doc = await fetchOpenIdDiscoveryDocument(discoveryUrl);
    await applyOpenIdDiscoveryDocument(oauth, doc);
    return;
  }
  await oauth.loadDiscoveryDocument(discoveryUrl);
}

function discoveryFailureMessage(err: unknown): string {
  if (typeof err === 'string' && err.length > 0) {
    return err;
  }
  if (err && typeof err === 'object') {
    const rec = err as Record<string, unknown>;
    const status = rec['status'];
    if (typeof status === 'number') {
      if (status === 0) {
        if (isCapacitorNative()) {
          return 'Cannot reach the sign-in service (HTTP 0). Check mobile data or Wi‑Fi, then try again.';
        }
        return (
          'Cannot reach the API from the emulator (HTTP 0). Start Spring Boot with dev profile on the host ' +
          '(./startService.sh) and ensure native apiBaseUrl is http://10.0.2.2:8080/api.'
        );
      }
      if (status === 502) {
        return (
          'The API returned 502 when loading OpenID configuration. Start the Spring backend on port 8080 ' +
          'and set OIDC_ISSUER_URI to your Supabase issuer.'
        );
      }
      return `OpenID discovery failed (HTTP ${status}).`;
    }
    const msg = rec['message'];
    if (typeof msg === 'string' && msg.length > 0) {
      return msg;
    }
  }
  return isCapacitorNative()
    ? 'OpenID discovery failed. Check network connectivity, or use email sign-in below.'
    : 'OpenID discovery failed. Check that the Spring API is running and reachable from the device.';
}

export async function setupOidcDiscovery(runtime: RuntimeConfigService, oauth: OAuthService): Promise<void> {
  const issuer = runtime.oidcIssuer;
  const clientId = runtime.oidcClientId;
  if (!issuer || !clientId) {
    oidcDiscoveryState.loaded = false;
    oidcDiscoveryState.errorMessage = null;
    return;
  }
  const redirectUri =
    runtime.oidcRedirectUri ||
    (typeof document !== 'undefined' ? new URL('auth/callback', document.baseURI).toString() : '');
  const postLogoutRedirectUri =
    runtime.oidcPostLogoutRedirectUri ||
    (typeof document !== 'undefined' ? new URL('.', document.baseURI).toString() : '');
  const discoveryUrls = openIdDiscoveryUrls(runtime);
  const primaryDiscoveryUrl = discoveryUrls[0];
  const cfg: AuthConfig = {
    issuer,
    clientId,
    redirectUri,
    postLogoutRedirectUri,
    responseType: 'code',
    scope: runtime.oidcScopes || 'openid profile email',
    strictDiscoveryDocumentValidation: false,
    showDebugInformation: false,
    timeoutFactor: 0.75,
    requireHttps: primaryDiscoveryUrl.startsWith('http://') ? false : undefined,
    sessionChecksEnabled: false,
  };
  oauth.configure(cfg);

  let lastErr: unknown = null;
  for (let attempt = 1; attempt <= DISCOVERY_RETRY_ATTEMPTS; attempt++) {
    for (const discoveryUrl of discoveryUrls) {
      try {
        await loadDiscoveryDocument(oauth, discoveryUrl);
        oidcDiscoveryState.loaded = true;
        oidcDiscoveryState.errorMessage = null;
        LoggerService.log('[OIDC] Discovery loaded', { discoveryUrl, attempt });
        return;
      } catch (err) {
        lastErr = err;
        LoggerService.error(`[OIDC] Discovery failed (${discoveryUrl}, attempt ${attempt})`, err);
      }
    }
    if (attempt < DISCOVERY_RETRY_ATTEMPTS) {
      await sleep(DISCOVERY_RETRY_DELAY_MS * attempt);
    }
  }

  oidcDiscoveryState.loaded = false;
  oidcDiscoveryState.errorMessage = discoveryFailureMessage(lastErr);
}
